import { PageHeader } from '../components/PageHeader';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import backend from '../services/backend';
import { PuzzlePieceIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../catalyst/button';
import { Input } from '../catalyst/input';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm, type SubmitHandler } from 'react-hook-form';
import { usePlatformData } from '../hooks/usePlatformData';
import { readApiErrorMessage } from '../lib/apiErrorMessage';
import { ErrorMessage, FieldGroup, Field, Fieldset, Label } from '../catalyst/fieldset';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '../components/Toast';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { supertypeSchema, type SupertypeFormData } from './supertypeView/types';
import { CategoriesSection } from './supertypeView/CategoriesSection';
import { ConfirmDialog } from '../components/ConfirmDialog';

export default function SupertypeView() {
  const { setTitle } = useDocumentTitle('Supertype View');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const platformData = usePlatformData();

  const queryClient = useQueryClient();
  const supertypeQuery = useQuery({
    queryKey: ['supertype', id],
    queryFn: () => backend.getSupertype(id!).then((res) => res.data),
    enabled: !!id && id !== 'new',
  });

  const mutation = useMutation({
    mutationFn: (data: SupertypeFormData) => {
      const payload = {
        name: data.name,
        description: data.description,
        specs: {
          categoryScoreMultiplier: data.categoryScoreMultiplier,
          categories: data.categories,
        },
      };
      if (id === 'new') {
        return backend.postSupertype(payload);
      } else {
        return backend.patchSupertype(id!, payload);
      }
    },
    onSuccess: (data) => {
      toast.success('Supertype saved successfully');
      queryClient.setQueryData(['supertype', id], { item: data.data });
      navigate(`/supertypes`);
    },
    onError: (error) => {
      // The server names the offending category, so show its message rather than a
      // generic failure the user cannot act on.
      toast.error(readApiErrorMessage(error) ?? 'Failed to save supertype');
    },
  });

  const form = useForm<SupertypeFormData>({
    defaultValues: {
      name: '',
      description: '',
      categoryScoreMultiplier: 0.8,
      categories: [],
    },
    // zodResolver's generic doesn't unify with our schema's inferred output
    // shape (the category tuples get widened during inference). Cast is
    // intentional and local; the runtime behaviour is unchanged.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(supertypeSchema as any),
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (supertypeQuery.data) {
      setTitle(supertypeQuery.data.item.name + ' - Supertype View');
    }
  }, [supertypeQuery.data]);

  useEffect(() => {
    if (supertypeQuery.data) {
      form.reset({
        name: supertypeQuery.data.item.name,
        description: supertypeQuery.data.item.description,
        categoryScoreMultiplier: supertypeQuery.data.item.specs.categoryScoreMultiplier,
        categories: supertypeQuery.data.item.specs.categories,
      });
    }
  }, [supertypeQuery.data, form.reset]);

  const categories = useFieldArray({
    control: form.control,
    name: 'categories',
  });

  const handleSave: SubmitHandler<SupertypeFormData> = (data) => mutation.mutate(data);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => backend.deleteSupertype(id!),
    onSuccess: () => {
      toast.success('Supertype deleted successfully');
      navigate('/supertypes');
    },
    onError: () => {
      toast.error('Failed to delete supertype');
      setIsDeleteConfirmOpen(false);
    },
  });

  const errors = form.formState.errors;

  if (id !== 'new' && supertypeQuery.isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <PageHeader
        title="Supertype"
        description="View your supertype"
        icon={<PuzzlePieceIcon className="size-6" />}
        showBackButton={true}
      >
        <Button plain onClick={() => setIsDeleteConfirmOpen(true)}>
          <span className="text-red-500 text-nowrap flex items-center gap-2">
            <TrashIcon className="size-4" />
            Delete
          </span>
        </Button>
      </PageHeader>

      <form onSubmit={form.handleSubmit(handleSave)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="p-4 rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700">
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Name</Label>
                  <Input {...form.register('name', { required: 'Name is required' })} />
                  {errors.name && <ErrorMessage className="text-red-500">{errors.name.message}</ErrorMessage>}
                </Field>
              </FieldGroup>

              <FieldGroup>
                <Field>
                  <Label>Description</Label>
                  <Input {...form.register('description')} />
                </Field>
              </FieldGroup>
            </Fieldset>
          </div>

          <CategoriesSection
            form={form}
            categories={categories}
            availableCategories={platformData.categories.data?.pageCategories ?? []}
          />
        </div>

        <div className="flex items-center gap-2 justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={isDeleteConfirmOpen}
        title="Delete Supertype"
        message="Are you sure you want to delete this supertype? This action cannot be undone."
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setIsDeleteConfirmOpen(false)}
      />
    </div>
  );
}
